import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft,
  FiExternalLink,
  FiGithub,
  FiSmartphone,
  FiX,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import projects, { getProject } from '../../projectsData';
import Navbar from '../Navbar/Navbar';
import {
  Page,
  Wrap,
  Back,
  Meta,
  Pill,
  MetaText,
  Title,
  Lead,
  Gallery,
  GalleryRow,
  GalleryItem,
  Lightbox,
  LBImg,
  LBBtn,
  LBCount,
  Section,
  Label,
  Body,
  TechRow,
  TechChip,
  Cta,
  CtaRow,
  Nav,
  NavLink,
  NotFound,
} from './ProjectDetail.elements';

const fade = (i = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay: 0.08 * i, ease: 'easeOut' },
});

const LINK_META = {
  web: { icon: FiExternalLink, fallback: 'Visit website' },
  github: { icon: FiGithub, fallback: 'View on GitHub' },
  ios: { icon: FaApple, fallback: 'App Store' },
  android: { icon: FaGooglePlay, fallback: 'Google Play' },
  app: { icon: FiSmartphone, fallback: 'Open the app' },
};

function ProjectDetail() {
  const { slug } = useParams();
  const project = getProject(slug);
  const [lb, setLb] = useState(-1); // lightbox index, -1 = closed

  const gallery = project ? project.gallery || [] : [];
  const close = useCallback(() => setLb(-1), []);
  const prev = useCallback(
    () => setLb((i) => (i - 1 + gallery.length) % gallery.length),
    [gallery.length]
  );
  const next = useCallback(
    () => setLb((i) => (i + 1) % gallery.length),
    [gallery.length]
  );

  useEffect(() => {
    if (lb < 0) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lb, close, prev, next]);

  if (!project) {
    return (
      <Page>
        <Navbar toggle={() => {}} />
        <Wrap>
          <Back to="/">
            <FiArrowLeft /> Back to home
          </Back>
          <NotFound>
            <h1>Project not found</h1>
            <p>The project you’re looking for doesn’t exist.</p>
          </NotFound>
        </Wrap>
      </Page>
    );
  }

  const idx = projects.findIndex((p) => p.slug === slug);
  const prevP = idx > 0 ? projects[idx - 1] : null;
  const nextP = idx < projects.length - 1 ? projects[idx + 1] : null;

  const canonical = `https://enesgungor.com/project/${project.slug}`;
  const ogImg =
    project.gallery && project.gallery[0]
      ? `https://enesgungor.com${project.gallery[0]}`
      : 'https://enesgungor.com/og-image.jpg';
  const metaTitle = `${project.title} — Project | Enes Güngör`;

  return (
    <Page>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={project.short} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={project.short} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImg} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={project.short} />
        <meta name="twitter:image" content={ogImg} />
      </Helmet>
      <Navbar toggle={() => {}} />
      <Wrap>
        <Back to="/">
          <FiArrowLeft /> Back to home
        </Back>

        <Meta {...fade(0)}>
          <Pill>{project.category}</Pill>
          <MetaText>{project.company}</MetaText>
          <MetaText>•</MetaText>
          <MetaText>{project.year}</MetaText>
        </Meta>

        <Title {...fade(1)}>{project.title}</Title>
        <Lead {...fade(2)}>{project.short}</Lead>

        {gallery.length > 0 && (
          <Gallery>
            <GalleryItem
              $hero
              onClick={() => setLb(0)}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <img src={gallery[0]} alt={`${project.title} 1`} />
            </GalleryItem>
            {gallery.length > 1 && (
              <GalleryRow>
                {gallery.slice(1).map((src, i) => (
                  <GalleryItem
                    key={src}
                    onClick={() => setLb(i + 1)}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.45,
                      delay: 0.05 * (i + 1),
                      ease: 'easeOut',
                    }}
                  >
                    <img src={src} alt={`${project.title} ${i + 2}`} />
                  </GalleryItem>
                ))}
              </GalleryRow>
            )}
          </Gallery>
        )}

        <Section>
          <Label>About the project</Label>
          <Body>{project.description}</Body>
        </Section>

        <Section>
          <Label>Tech stack</Label>
          <TechRow>
            {project.tech.map((t) => (
              <TechChip key={t}>{t}</TechChip>
            ))}
          </TechRow>
        </Section>

        {project.links && project.links.length > 0 && (
          <Section>
            <Label>Links</Label>
            <CtaRow>
              {project.links.map((l) => {
                const meta = LINK_META[l.type] || LINK_META.web;
                const Icon = meta.icon;
                return (
                  <Cta
                    key={l.url}
                    href={l.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon />
                    {l.label || meta.fallback}
                  </Cta>
                );
              })}
            </CtaRow>
          </Section>
        )}

        <Nav>
          {prevP ? (
            <NavLink to={`/project/${prevP.slug}`}>
              ← Previous
              <strong>{prevP.title}</strong>
            </NavLink>
          ) : (
            <span />
          )}
          {nextP ? (
            <NavLink className="next" to={`/project/${nextP.slug}`}>
              Next →
              <strong>{nextP.title}</strong>
            </NavLink>
          ) : (
            <span />
          )}
        </Nav>
      </Wrap>

      <AnimatePresence>
        {lb >= 0 && (
          <Lightbox
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <LBBtn className="close" onClick={close} aria-label="Close">
              <FiX />
            </LBBtn>
            {gallery.length > 1 && (
              <>
                <LBBtn
                  className="prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    prev();
                  }}
                  aria-label="Previous"
                >
                  <FiChevronLeft />
                </LBBtn>
                <LBBtn
                  className="next"
                  onClick={(e) => {
                    e.stopPropagation();
                    next();
                  }}
                  aria-label="Next"
                >
                  <FiChevronRight />
                </LBBtn>
              </>
            )}
            <LBImg
              key={gallery[lb]}
              src={gallery[lb]}
              alt={`${project.title} ${lb + 1}`}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.22 }}
            />
            {gallery.length > 1 && (
              <LBCount>
                {lb + 1} / {gallery.length}
              </LBCount>
            )}
          </Lightbox>
        )}
      </AnimatePresence>
    </Page>
  );
}

export default ProjectDetail;
